// src/utils/units.js
// D-291 (Stabilization P3c-6) - which units are COUNTED and which are MEASURED.
//
// WHY THIS FILE EXISTS
// A quantity like 2.5 "Nos" is a typing mistake (you cannot buy half a
// fitting), but 15.5 RM of copper pipe or 2.04 SqM of insulation is a real
// measurement. Ravi (21 Oct): "qty me decimal nahi aana chahiye" for counted
// things - so the unit decides:
//   counted units  -> whole numbers only (1, 2, 3 ...)
//   measured units -> up to 2 decimals
// Every screen and service that checks a quantity asks THIS file, so the rule
// is the same everywhere (PO entry and amend now; MRF / GRN in the MRF flow).
//
// Pure (no Firebase) -> covered by src/utils/units.test.js (Jest).

// Lower-case, no spaces / dots: "Nos." -> "nos", "Sq M" -> "sqm".
export const normaliseUnit = (unit) => String(unit ?? "").toLowerCase().replace(/[\s.]+/g, "");

// Units you count. Anything else (Mtr, RM, Rmt, SqM, Sqft, Kg, Ltr ...) is
// measured. Add a unit here only if it can never be a fraction.
export const COUNT_UNITS = [
  "nos", "no", "number", "numbers",
  "pcs", "pc", "piece", "pieces",
  "set", "sets", "pair", "pairs",
  "box", "boxes", "bundle", "bundles",
  "roll", "rolls", "coil", "coils",
  "lot", "lots", "ls", "lumpsum",
  "unit", "units", "each", "ea", "job", "jobs",
];

export const MEASURED_QTY_DECIMALS = 2;

// P3c-7 - the ONE unit list for the PO screens (Direct PO + Amend PO).
// Counted units first, then measured ones. A unit that comes from Item Master
// is never re-spelled to this list - it is saved exactly as Item Master has
// it (the GRN compares it with the item's purchase_unit).
export const UNIT_OPTIONS = [
  "Nos", "Pcs", "Set", "Pair", "Box", "Bundle", "Roll", "Coil", "Lot", "LS",
  "Mtr", "Rmt", "SqM", "Sqft", "Kg", "Ltr",
];

// true when a unit is filled in ("" / spaces / null = missing).
export const hasUnit = (unit) => String(unit ?? "").trim() !== "";

// The picker's options: the list, plus the current value first when it is
// not in the list (an old free-text unit is shown, never silently dropped).
export const unitOptionsFor = (current) =>
  hasUnit(current) && !UNIT_OPTIONS.includes(current) ? [current, ...UNIT_OPTIONS] : UNIT_OPTIONS;

export const isCountUnit = (unit) => COUNT_UNITS.includes(normaliseUnit(unit));

// The step for a quantity box: 1 for counted units, 0.01 for measured ones.
export const qtyStepFor = (unit) => (isCountUnit(unit) ? 1 : 0.01);

/**
 * @returns {{ ok: boolean, reason: string }}
 * Only the decimals are checked here; "more than 0" is the caller's check.
 */
export function checkQtyForUnit(qty, unit) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return { ok: false, reason: "quantity is not a number" };
  if (isCountUnit(unit)) {
    return Number.isInteger(n)
      ? { ok: true, reason: "" }
      : { ok: false, reason: `${String(unit).trim() || "This unit"} is counted in whole numbers (1, 2, 3 ...) - no decimals` };
  }
  const scaled = n * 10 ** MEASURED_QTY_DECIMALS;
  return Math.abs(scaled - Math.round(scaled)) < 1e-9
    ? { ok: true, reason: "" }
    : { ok: false, reason: `quantity can have at most ${MEASURED_QTY_DECIMALS} decimals` };
}